// ─────────────────────────────────────────────────────────────
// INFINI — PostHog Initializer
// Call initPostHog() once in main.tsx before React mounts.
// ─────────────────────────────────────────────────────────────

import posthog from 'posthog-js';

// ─── Environment config ───────────────────────────────────────

const POSTHOG_KEY  = import.meta.env.VITE_POSTHOG_API_KEY  as string | undefined;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST     as string | undefined;
const IS_DEV       = import.meta.env.DEV as boolean;

/**
 * Whether PostHog is initialised and ready to accept events.
 * Read this before calling posthog.capture() directly (prefer
 * the useAnalytics hook, which handles this guard internally).
 */
export function isAnalyticsReady(): boolean {
  return posthog.__loaded === true;
}

// ─── Initializer ─────────────────────────────────────────────

/**
 * Must be called once, at the top of main.tsx, before ReactDOM.render().
 *
 * In development (VITE_POSTHOG_API_KEY absent or empty):
 *   - PostHog is NOT initialised.
 *   - All capture() calls silently no-op via the isAnalyticsReady() guard.
 *   - A console.info message confirms dev mode.
 *
 * In production:
 *   - PostHog initialises with the provided key and host.
 *   - Session recording is DISABLED by default (GDPR / cost).
 *   - Autocapture is DISABLED — all events are intentional.
 *   - Pageview capture is DISABLED — we fire pioneer_landing_viewed manually.
 */
export function initPostHog(): void {
  if (!POSTHOG_KEY || IS_DEV) {
    console.info(
      '[INFINI Analytics] PostHog skipped — running in dev mode. ' +
      'Set VITE_POSTHOG_API_KEY in .env.local to enable.'
    );
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host:                    POSTHOG_HOST ?? 'https://app.posthog.com',

    // ── Capture behaviour ────────────────────────────────────
    autocapture:                 false, // manual events only
    capture_pageview:            false, // we fire pioneer_landing_viewed ourselves
    capture_pageleave:           true,  // useful for drop-off signal

    // ── Session recording ────────────────────────────────────
    disable_session_recording:   true,  // re-enable when GDPR consent flow exists

    // ── Privacy ──────────────────────────────────────────────
    mask_all_text:               false,
    mask_all_element_attributes: false,
    respect_dnt:                 true,  // honour browser Do Not Track

    // ── Performance ──────────────────────────────────────────
    // Batch events — flushes every 3 s or when batch reaches 20 events.
    request_batching:            true,

    // ── Persistence ─────────────────────────────────────────
    // 'localStorage+cookie' falls back to cookie in Safari ITP environments.
    persistence:                 'localStorage+cookie',

    loaded: (ph) => {
      // Attach app metadata to every subsequent event as super-properties.
      ph.register({
        app_version:  import.meta.env.VITE_APP_VERSION ?? 'unknown',
        pioneer_phase: 1,
        currency:     'THB',
      });

      if (IS_DEV) {
        // Safety net — should never reach here, but log clearly if it does.
        console.warn('[INFINI Analytics] PostHog loaded in dev — check env guard.');
      }
    },
  });
}

// Re-export the posthog instance so the hook can import it directly
// without duplicating the import path everywhere.
export { posthog };
